import { Children, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Text, Tooltip } from '@optiaxiom/react';

type TruncatedListProps = {
  children: ReactNode;
  lineClamp?: number;
  gap?: '0' | '2' | '4' | '6' | '8' | '10' | '12' | '16' | '20' | '24' | '32' | '40' | '48' | '64';
};

const OVERFLOW_INDICATOR_WIDTH = 24;

export function TruncatedList({ children, lineClamp = 1, gap = '6' }: TruncatedListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const prevWidthRef = useRef<number>(0);
  const lastVisibleCountRef = useRef<number | null>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const childArray = Children.toArray(children);
  const gapPx = parseInt(gap, 10);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        const prevWidth = prevWidthRef.current;
        prevWidthRef.current = newWidth;
        if (prevWidth !== 0 && prevWidth !== newWidth) {
          setVisibleCount(null);
        }
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setVisibleCount(null);
  }, [children, lineClamp]);

  useEffect(() => {
    if (
      !containerRef.current ||
      !measureRef.current ||
      childArray.length === 0 ||
      visibleCount !== null
    ) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const itemElements = Array.from(measureRef.current.children);

    if (itemElements.length === 0) {
      setVisibleCount(childArray.length);
      return;
    }

    let currentLineWidth = 0;
    let currentLineCount = 1;
    let count = 0;

    for (let i = 0; i < itemElements.length; i++) {
      const itemWidth = (itemElements[i] as HTMLElement).offsetWidth;
      const widthWithGap = itemWidth + (currentLineWidth > 0 ? gapPx : 0);
      const hasMoreItems = i < itemElements.length - 1;
      const isLastAllowedLine = currentLineCount >= lineClamp;
      const reservedSpace =
        hasMoreItems && isLastAllowedLine ? OVERFLOW_INDICATOR_WIDTH + gapPx : 0;

      if (currentLineWidth + widthWithGap + reservedSpace <= containerWidth) {
        currentLineWidth += widthWithGap;
        count++;
      } else if (currentLineCount < lineClamp) {
        currentLineCount++;
        currentLineWidth = itemWidth;
        count++;
      } else {
        break;
      }
    }

    const newCount = count || childArray.length;
    lastVisibleCountRef.current = newCount;
    setVisibleCount(newCount);
  }, [childArray, visibleCount, lineClamp, gapPx]);

  const isMeasuring = visibleCount === null;
  const displayCount = visibleCount ?? lastVisibleCountRef.current ?? childArray.length;
  const remainingCount = childArray.length - displayCount;

  // Extract text content from remaining children for tooltip
  const remainingTooltip = useMemo(() => {
    if (remainingCount === 0) return '';
    return childArray
      .slice(displayCount)
      .map(child => {
        // Extract text content from Badge or other components
        if (typeof child === 'object' && child !== null && 'props' in child) {
          const props = child.props as { children?: ReactNode };
          return typeof props.children === 'string' ? props.children : '';
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }, [childArray, displayCount, remainingCount]);

  if (childArray.length === 0) {
    return null;
  }

  return (
    <Flex
      ref={containerRef}
      flexDirection="row"
      gap={gap}
      flexWrap="wrap"
      overflow="hidden"
      flex="1"
      alignItems="center"
    >
      {isMeasuring && (
        <Flex
          ref={measureRef}
          flexDirection="row"
          gap={gap}
          flexWrap="wrap"
          style={{ visibility: 'hidden', position: 'absolute' }}
        >
          {childArray}
        </Flex>
      )}
      {childArray.slice(0, displayCount)}
      {remainingCount > 0 && (
        <Tooltip content={remainingTooltip}>
          <Text fontSize="sm" color="fg.secondary" cursor="default">
            +{remainingCount}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
}
